import { useMutation } from "@tanstack/react-query";
import { t } from "@lingui/core/macro";
import { i18n } from "@app/i18n";
import type { SignUpDto } from "shared/dto/auth";
import { authMessageDescriptor } from "shared/dto/auth/messages";
import { apiClient } from "@/lib/ApiClient";
import { RateLimitError } from "@/lib/rateLimitNotice";

/** 서버 /api/sign-up 의 요청 본문 = 가입 폼이 검증한 값(shared/dto/auth). */
export type SignUpInput = SignUpDto;

/** POST /api/sign-up 로 계정을 생성한다. 로그인은 별도로(better-auth) 처리한다. */
export function useSignUpMutation() {
  return useMutation({
    mutationFn: async (input: SignUpInput) => {
      const res = await apiClient.api["sign-up"].$post({ json: input });
      // rate limit 에 걸렸는지 — 안내 토스트는 apiClient 의 fetch 래퍼가 이미 띄웠으므로
      // 호출부(로그인 페이지)가 "가입 실패" 토스트를 겹쳐 띄우지 않게 전용 에러로 던진다.
      // (hono RPC 는 status 를 성공 코드로 좁혀 두므로 number 로 넓혀 비교한다.)
      if ((res.status as number) === 429) throw new RateLimitError();
      // 응답이 실패했는지
      const isResNotOk = !res.ok;
      if (isResNotOk) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        // 서버가 shared/dto/auth 스키마로 되돌려준 검증 메시지 키인지(키면 번역해서 보여준다).
        const descriptor = authMessageDescriptor(body.error);
        const message = descriptor ? i18n._(descriptor) : body.error;
        throw new Error(message ?? t(i18n)`가입에 실패했습니다.`);
      }
      return res.json();
    },
  });
}
